import { Box } from '@factorialco/deprecated-design-system'
import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { isExpensableReporter, isOwnExpensable, isPendingExpensable } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

import TitleHeader from './TitleHeader'
import AdditionalInformationWidget from './widgets/AdditionalInformationWidget'
import AttachedDocumentsWidget from './widgets/AttachedDocumentsWidget'
import BudgetAndProjectWidget from './widgets/BudgetAndProjectWidget'
import CostCentersWidget from './widgets/CostCentersWidget'
import DocumentInformationWidget from './widgets/DocumentInformationWidget'
import ExpenseInformationWidget from './widgets/ExpenseInformationWidget'
import ExpensePaymentWidget from './widgets/PaymentWidget'

interface ConfirmationScreenProps {
  expensable: Expensable
  customizationFields: CustomizationField[]
  editable: boolean
  children?: React.ReactNode
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
  onEdit: (scrollTo?: string | undefined) => void
  adeTermsAccepted?: boolean
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  showBudgetAndProject?: boolean
  canEditBudgetAndProject?: boolean
  showCostCenters?: boolean
  showDescription?: boolean
  showInternalReference?: boolean
}
const ConfirmationScreen = ({
  expensable,
  customizationFields,
  editable,
  children,
  resourceType,
  onEdit,
  adeTermsAccepted,
  taxAgencyOrganization,
  showBudgetAndProject,
  canEditBudgetAndProject,
  showCostCenters,
  showDescription,
  showInternalReference,
}: ConfirmationScreenProps) => {
  const { employee: currentEmployee } = useCurrentSession()
  const isDraft = expensable.status === 'draft'
  const isExpensableOwner = isOwnExpensable(expensable, currentEmployee)
  const isReporter = isExpensableReporter(expensable, currentEmployee)
  const canReview = expensable.currentUserApprovalStatus === 'pending'
  const filesEditable =
    (editable && (isExpensableOwner || isReporter) && (isDraft || expensable.status === 'changes_requested')) ||
    (editable && isPendingExpensable(expensable))
  const showSubmitterText = isDraft && !canReview && isExpensableOwner
  const showReviewerText = canReview && !isExpensableOwner

  const anyTitleHeader = showSubmitterText || showReviewerText

  return (
    <Box gap="s16" paddingY="s32">
      <Box paddingBottom={anyTitleHeader ? 's0' : 's16'}>
        <BasicDetails expensable={expensable} />
      </Box>
      {showSubmitterText && (
        <TitleHeader
          titleLabel={i18n.t('expenses_v2.confirmation_screen.title_submitter')}
          subtitleLabel={i18n.t('expenses_v2.confirmation_screen.subtitle_submitter')}
        />
      )}
      {showReviewerText && (
        <TitleHeader
          titleLabel={i18n.t('expenses_v2.confirmation_screen.title_reviewer')}
          subtitleLabel={i18n.t('expenses_v2.confirmation_screen.subtitle_reviewer')}
        />
      )}
      <ExpenseInformationWidget
        expensable={expensable}
        customizationFields={customizationFields}
        editable={editable}
        onEdit={() => onEdit('basicInformation')}
        isOwnExpensable={isExpensableOwner}
        resourceType={resourceType}
      />

      <DocumentInformationWidget
        expensable={expensable}
        onEdit={() => onEdit('documentInformation')}
        editable={editable}
      />

      <ExpensePaymentWidget
        expensable={expensable}
        customizationFields={customizationFields}
        onEdit={() => onEdit('paymentInformation')}
        editable={editable}
      />

      <AdditionalInformationWidget
        expensable={expensable}
        onEdit={() => onEdit('additionalInformation')}
        editable={editable}
        showDescription={showDescription && editable}
        showInternalReference={showInternalReference && editable}
      />
      <AttachedDocumentsWidget
        expensable={expensable}
        onEdit={() => onEdit('files')}
        editable={filesEditable}
        adeTermsAccepted={adeTermsAccepted}
        taxAgencyOrganization={taxAgencyOrganization}
      />
      <BudgetAndProjectWidget
        expensable={expensable}
        onEdit={() => onEdit('budgetAndProject')}
        editable={editable && (canEditBudgetAndProject ?? true)}
        showWhenEmpty={showBudgetAndProject && editable && (canEditBudgetAndProject ?? true)}
      />
      <CostCentersWidget
        expensable={expensable}
        onEdit={() => onEdit('costCenters')}
        editable={editable}
        showWhenEmpty={showCostCenters && editable}
      />
      {children}
    </Box>
  )
}

export default ConfirmationScreen
